import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { generateSubjectLines, generateTemplate, type GenerateSubjectLinesResponse } from "src/services/EngagementAPIs";
import type { CampaignDraft } from "src/services/CampaignDraftTypes";
import { getThrownApiDetail, showApiDetailToast } from "src/utils/showApiDetailToast";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type { CampaignData } from "../../../../../EngagementInterfaces";
import {
  draftContentToTemplate,
  readString,
  templateToDraftContent,
  withoutLists,
} from "../../../../shared/CampaignDraftUtils";
import { useCampaignDraft, type CampaignDraftState } from "../../../../shared/UseCampaignDraft";
import { useDraftWarnings } from "../../../../shared/UseDraftWarnings";
import { buildAiDraftData, restoreAiDraft } from "../AiCampaignDraft";
import {
  clearWizardSnapshot,
  readWizardSnapshot,
  writeWizardSnapshot,
} from "../WizardSnapshot";
import {
  EMAIL_WIZARD_STEPS,
  createInitialAiCampaignForm,
  getToneLabelKey,
} from "../AiCampaignConstants";
import type {
  AiCampaignForm,
  AiCampaignStepKey,
  EmailDesign,
  GeneratedTemplate,
  UpdateAiCampaignForm,
} from "../AiCampaignTypes";
import {
  getEmailAdjacentStepIndex,
  getEmailStepValidationError,
  parseGeneratedDesign,
  resolveCampaignType,
  resolveSubCampaign,
} from "../AiCampaignUtils";
import { useCampaignBasics } from "./UseCampaignBasics";

const REVIEW_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "reviewDetails");
const CREATE_STEP_INDEX = EMAIL_WIZARD_STEPS.findIndex(step => step.key === "createCampaign");

const ERROR_KEY = "campaignFlow.aiWizard.errors";

interface AiEmailWizardOptions {
  initialStepIndex?: number;
  persist?: boolean;
  initialDraft?: CampaignDraft | null;
  /** Lets a host that spans several wizards (the chat flow) share one draft between them. */
  draftController?: CampaignDraftState;
}

const clampResumedStep = (stepIndex: number, hasTemplate: boolean) =>
  stepIndex === CREATE_STEP_INDEX && !hasTemplate ? REVIEW_STEP_INDEX : stepIndex;

export const useAiEmailWizard = (
  campaignData: Partial<CampaignData>,
  {
    initialStepIndex = 0,
    persist = true,
    initialDraft = null,
    draftController,
  }: AiEmailWizardOptions = {}
) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const storageKey = useLocation().key;
  const [saved] = useState(() => (persist ? readWizardSnapshot(storageKey) : null));
  const ownDraft = useCampaignDraft(initialDraft);
  const draft = draftController ?? ownDraft;
  const { syncUpdatedAt, closeDraft } = draft;
  const { missingLists, isNameTaken } = useDraftWarnings(initialDraft);
  const [resumed] = useState(() => {
    if (!initialDraft || saved) {
      return null;
    }
    const restored = restoreAiDraft(initialDraft);
    return {
      ...restored,
      sendTo: withoutLists(restored.sendTo, missingLists),
      excludeList: withoutLists(restored.excludeList, missingLists),
      stepIndex:
        missingLists.length > 0 || isNameTaken
          ? 0
          : clampResumedStep(restored.stepIndex, restored.hasTemplate),
    };
  });
  const [stepIndex, setStepIndex] = useState(() => {
    if (resumed) {
      return resumed.stepIndex;
    }
    if (!saved) {
      return initialStepIndex;
    }
    const index = Math.min(Math.max(saved.stepIndex, 0), EMAIL_WIZARD_STEPS.length - 1);
    return clampResumedStep(index, Boolean(saved.template));
  });
  const [form, setForm] = useState<AiCampaignForm>(
    () =>
      saved?.form ?? resumed?.form ?? createInitialAiCampaignForm(campaignData.campaignName ?? "")
  );
  const [returnToReview, setReturnToReview] = useState(false);
  const [template, setTemplate] = useState<GeneratedTemplate | null>(
    () => saved?.template ?? draftContentToTemplate(initialDraft?.content)
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const generatedSignatureRef = useRef<string | null>(
    saved?.signature ??
      (initialDraft ? readString(initialDraft.data.generation_signature) || null : null)
  );
  const [subjectLines, setSubjectLines] = useState<GenerateSubjectLinesResponse | null>(null);
  const [isLoadingSubjectLines, setIsLoadingSubjectLines] = useState(false);
  const subjectSignatureRef = useRef<string | null>(null);

  const stepKey = EMAIL_WIZARD_STEPS[stepIndex].key;
  const furthestIndexRef = useRef(stepIndex);
  furthestIndexRef.current = Math.max(furthestIndexRef.current, stepIndex);
  const {
    sendTo,
    excludeList,
    replyTo,
    nameError,
    isCheckingName,
    audienceOptions,
    excludeOptions,
    companyName,
    isListsLoading,
    senderDetails,
    setSendTo,
    setExcludeList,
    setReplyTo,
    clearNameError,
    verifyCampaignName,
  } = useCampaignBasics(campaignData, saved ?? resumed ?? {}, stepKey === "senderRecipients");

  useEffect(() => {
    if (!persist) {
      return;
    }
    writeWizardSnapshot(storageKey, {
      stepIndex,
      form,
      sendTo,
      excludeList,
      replyTo,
      template,
      signature: generatedSignatureRef.current,
    });
  }, [excludeList, form, persist, replyTo, sendTo, stepIndex, storageKey, template]);

  useEffect(
    () => () => {
      if (persist) {
        clearWizardSnapshot(storageKey);
      }
    },
    [persist, storageKey]
  );

  const validationContext = useMemo(() => ({ sendTo, replyTo }), [replyTo, sendTo]);
  const canProceed = useMemo(
    () => !isCheckingName && !getEmailStepValidationError(stepKey, form, validationContext),
    [form, isCheckingName, stepKey, validationContext]
  );

  const updateForm = useCallback<UpdateAiCampaignForm>((key, value) => {
    if (key === "campaignName") {
      clearNameError();
    }
    setForm(previous => {
      const next = { ...previous, [key]: value };
      if (key === "campaignType" && value !== previous.campaignType) {
        next.subCampaign = null;
        next.customSubCampaign = "";
      }
      return next;
    });
  }, [clearNameError]);

  const buildGeneratePayload = useCallback(() => {
    const offer = form.offer.trim();
    return {
      category_links: form.selectedCategories,
      campaign_type: resolveCampaignType(form),
      campaign_subtype: resolveSubCampaign(form),
      campaign_name: form.campaignName.trim(),
      prompt: form.prompt.trim(),
      tone: form.tone ? [t(getToneLabelKey(form.tone))] : [],
      persona: form.persona.trim(),
      seasonal_tag: form.seasonalTag.trim(),
      cta_label: form.ctaLabel.trim(),
      cta_url: form.ctaUrl.trim(),
      lang: getApiLangFromI18n(i18n),
      ...(offer ? { offer } : {}),
    };
  }, [form, i18n, t]);

  const generate = useCallback(async (): Promise<boolean> => {
    const payload = buildGeneratePayload();
    const signature = JSON.stringify(payload);
    if (template && generatedSignatureRef.current === signature) {
      return true;
    }

    setIsGenerating(true);
    try {
      const result = await generateTemplate(
        draft.draftId === null ? payload : { ...payload, draft_id: draft.draftId }
      );
      if (!result.success) {
        if (!result.toastShown) {
          message.error(result.errorMessage || t("campaignFlow.aiWizard.errors.generateFailed"));
        }
        return false;
      }

      setTemplate({
        design: parseGeneratedDesign(result.design),
        html: typeof result.template === "string" ? result.template : "",
      });
      generatedSignatureRef.current = signature;
      syncUpdatedAt(result.draftUpdatedAt);
      return true;
    } finally {
      setIsGenerating(false);
    }
  }, [buildGeneratePayload, draft.draftId, syncUpdatedAt, t, template]);

  const loadSubjectLines = useCallback(
    async (force = false) => {
      const payload = buildGeneratePayload();
      const signature = JSON.stringify(payload);
      if (!force && subjectSignatureRef.current === signature) {
        return;
      }
      subjectSignatureRef.current = signature;
      setIsLoadingSubjectLines(true);
      try {
        setSubjectLines(await generateSubjectLines(payload));
      } catch (error) {
        const detail = getThrownApiDetail(error);
        if (detail) {
          showApiDetailToast(detail);
        } else {
          message.error(t(`${ERROR_KEY}.subjectLinesFailed`));
        }
      } finally {
        setIsLoadingSubjectLines(false);
      }
    },
    [buildGeneratePayload, t]
  );

  useEffect(() => {
    if (stepKey === "senderRecipients") {
      void loadSubjectLines();
    }
  }, [loadSubjectLines, stepKey]);

  const subjectSuggestions = useMemo(
    () => ({
      items: subjectLines?.subject_lines ?? [],
      bestSubjectLine: subjectLines?.best_subject_line,
      isLoading: isLoadingSubjectLines,
      refresh: () => void loadSubjectLines(true),
    }),
    [isLoadingSubjectLines, loadSubjectLines, subjectLines]
  );

  const goNext = useCallback(async () => {
    const errorKey = getEmailStepValidationError(stepKey, form, validationContext);
    if (errorKey) {
      message.error(t(errorKey));
      return;
    }
    if (
      stepKey === "campaignDetails" &&
      !(await verifyCampaignName(form.campaignName, draft.draftId))
    ) {
      return;
    }
    if (stepKey === "reviewDetails" && !(await generate())) {
      return;
    }
    if (returnToReview && stepKey !== "reviewDetails") {
      setReturnToReview(false);
      setStepIndex(REVIEW_STEP_INDEX);
      return;
    }
    setStepIndex(current => getEmailAdjacentStepIndex(current, 1, form));
  }, [
    draft.draftId,
    form,
    generate,
    returnToReview,
    stepKey,
    t,
    validationContext,
    verifyCampaignName,
  ]);

  const goToStep = useCallback(
    async (target: number) => {
      setReturnToReview(false);
      if (target <= stepIndex) {
        setStepIndex(target);
        return;
      }
      if (stepKey === "campaignDetails" && !(await verifyCampaignName(form.campaignName))) {
        return;
      }
      let index = stepIndex;
      while (index < target) {
        const errorKey = getEmailStepValidationError(EMAIL_WIZARD_STEPS[index].key, form, validationContext);
        if (errorKey) {
          message.error(t(errorKey));
          setStepIndex(index);
          return;
        }
        index = getEmailAdjacentStepIndex(index, 1, form);
      }
      setStepIndex(index);
    },
    [form, stepIndex, stepKey, t, validationContext, verifyCampaignName]
  );

  const editStep = useCallback((key: AiCampaignStepKey) => {
    setReturnToReview(true);
    setStepIndex(EMAIL_WIZARD_STEPS.findIndex(step => step.key === key));
  }, []);

  const goBack = useCallback(() => {
    if (returnToReview) {
      setReturnToReview(false);
      setStepIndex(REVIEW_STEP_INDEX);
      return;
    }
    if (stepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex(current => getEmailAdjacentStepIndex(current, -1, form));
  }, [form, navigate, returnToReview, stepIndex]);

  const saveTemplateDesign = useCallback((design: EmailDesign) => {
    setTemplate(current => (current ? { ...current, design } : current));
  }, []);

  const buildDraftData = useCallback(
    () => ({
      ...buildAiDraftData(
        { stepIndex, form, sendTo, excludeList, replyTo },
        form.tone ? [t(getToneLabelKey(form.tone))] : []
      ),
      generation_signature: generatedSignatureRef.current,
    }),
    // the generation signature is a ref that only changes together with `template`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeList, form, replyTo, sendTo, stepIndex, t, template]
  );

  const { save: saveToDraft, savedSnapshot } = draft;
  const saveDraft = useCallback(
    (design?: EmailDesign | null) =>
      saveToDraft(buildDraftData(), templateToDraftContent(template, design)),
    [buildDraftData, saveToDraft, template]
  );
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(buildDraftData()) !== savedSnapshot,
    [buildDraftData, savedSnapshot]
  );

  const reset = useCallback(
    (nextCampaignData: Partial<CampaignData>) => {
      closeDraft();
      setStepIndex(initialStepIndex);
      setForm(createInitialAiCampaignForm(nextCampaignData.campaignName ?? ""));
      setReturnToReview(false);
      setTemplate(null);
      setIsGenerating(false);
      setSubjectLines(null);
      setSendTo(nextCampaignData.sendTo ?? []);
      setExcludeList(nextCampaignData.excludeList ?? []);
      setReplyTo("");
      generatedSignatureRef.current = null;
      subjectSignatureRef.current = null;
    },
    [closeDraft, initialStepIndex, setExcludeList, setReplyTo, setSendTo]
  );

  return {
    stepIndex,
    stepKey,
    form,
    template,
    senderDetails,
    sendTo,
    excludeList,
    audienceOptions,
    excludeOptions,
    companyName,
    isListsLoading,
    nameError,
    isGenerating,
    canProceed,
    setSendTo,
    setReplyTo,
    setExcludeList,
    updateForm,
    goNext,
    goBack,
    editStep,
    goToStep,
    furthestIndex: furthestIndexRef.current,
    isEditingFromReview: returnToReview,
    subjectSuggestions,
    saveTemplateDesign,
    reset,
    draft: {
      draftId: draft.draftId,
      isSaving: draft.isSaving,
      hasUnsavedChanges,
      save: saveDraft,
      close: closeDraft,
    },
  };
};
